import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { chatApi, type Message } from '@/entities/message';
import { ApiError } from '@/shared/api/types';
import { QUERY_KEYS } from '@/shared/config/constants';

type MessagesCache = { messages: Message[] } | undefined;

// Mensagens de erro humanizadas para os codigos do backend.
// O backend emite ApiError com .code; mapeamos aqui o que o usuario ve.
const ERROR_MESSAGES: Record<string, string> = {
  MESSAGE_DELETE_WINDOW_EXPIRED: 'Tempo limite (7 min) para apagar expirou.',
  MESSAGE_DELETE_FORBIDDEN: 'Apenas o autor pode apagar a mensagem.',
  MESSAGE_ALREADY_DELETED: 'Esta mensagem ja foi apagada.',
  MESSAGE_NOT_FOUND: 'Mensagem nao encontrada.',
  NOT_CONVERSATION_PARTICIPANT: 'Voce nao faz parte dessa conversa.',
  MATCH_NOT_MUTUAL_ACCEPTED: 'O match nao esta mais ativo.',
};

const markAsDeletedLocally = (
  cache: MessagesCache,
  messageId: string,
  deletedAt: string,
): MessagesCache => {
  if (!cache) return cache;
  return {
    messages: cache.messages.map((m) =>
      m.id === messageId
        ? {
            ...m,
            isDeleted: true,
            deletedAt,
            content: '',
            fileUrl: null,
            fileName: null,
            fileType: null,
          }
        : m,
    ),
  };
};

/**
 * Mutation de soft delete com optimistic update.
 *
 * - Marca a mensagem como deletada na cache antes do round-trip pra
 *   resposta visual instantanea (estilo WhatsApp).
 * - Em caso de erro, faz rollback e mostra toast com mensagem amigavel
 *   baseada no code do backend.
 * - Sem invalidate em onSettled: o backend emite `message:deleted` no
 *   socket que o ChatConversationPage escuta e ja sincroniza todas as
 *   tabs/devices do user. Invalidate dispararia refetch desnecessario.
 */
export const useDeleteMessage = (matchId: string) => {
  const qc = useQueryClient();
  const cacheKey = QUERY_KEYS.MESSAGES(matchId);

  return useMutation({
    mutationFn: (messageId: string) => chatApi.deleteMessage(messageId),
    onMutate: async (messageId) => {
      await qc.cancelQueries({ queryKey: cacheKey });
      const previous = qc.getQueryData<MessagesCache>(cacheKey);
      qc.setQueryData<MessagesCache>(cacheKey, (prev) =>
        markAsDeletedLocally(prev, messageId, new Date().toISOString()),
      );
      return { previous };
    },
    onError: (err, _messageId, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData<MessagesCache>(cacheKey, ctx.previous);
      }
      const code = err instanceof ApiError ? err.code : undefined;
      toast.error(
        (code && ERROR_MESSAGES[code]) ?? 'Falha ao apagar a mensagem. Tente novamente.',
      );
    },
  });
};
